/**
 * Left rail: the controls used constantly during a listening session.
 */

import { useEffect, useState } from 'react';
import { registry } from '../../database';
import * as storage from '../../database/storage';
import type { SlotId, SystemSlotKey } from '../../types/system';
import { useApp } from '../../stores/appStore';
import { Note, SectionTitle, Segmented, Slider, Stat, Toggle, fmtTime } from './primitives';
import { CablePanel } from './CablePanel';

export function LeftPanel() {
  return (
    <>
      <div className="panel-head">
        <span>Control</span>
      </div>
      <div className="panel-body">
        <QuickSwap />
        <CablePanel />
        <ComparisonSection />
        <LevelMatchSection />
        <BankSection />
        <SavedSystems />
        <HistorySection />
      </div>
    </>
  );
}

function QuickSwap() {
  const activeSystem = useApp((s) => s.activeSystem);
  const openLibrary = useApp((s) => s.openLibrary);
  const profileFor = useApp((s) => s.profileFor);
  const blind = useApp((s) => s.blind);

  const slots: Array<{ id: SlotId; label: string }> = [
    { id: 'dac', label: 'DAC' },
    { id: 'amp', label: 'Amplifier' },
    { id: 'output', label: 'Output' },
  ];

  return (
    <div className="panel-section">
      <SectionTitle>Live swap — System {activeSystem}</SectionTitle>
      {slots.map((s) => {
        const p = profileFor(activeSystem, s.id);
        return (
          <button
            key={s.id}
            className="btn wide"
            style={{ justifyContent: 'flex-start', height: 40, marginBottom: 5, textAlign: 'left' }}
            onClick={() => openLibrary(s.id, activeSystem)}
          >
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
              <span className="tiny faint" style={{ letterSpacing: '0.1em' }}>{s.label}</span>
              <span
                className="bright"
                style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}
              >
                {blind ? '••••••' : p ? `${p.manufacturer} ${p.model}` : 'None selected'}
              </span>
            </span>
            <span className="faint">▸</span>
          </button>
        );
      })}
    </div>
  );
}

function ComparisonSection() {
  const comparison = useApp((s) => s.project.comparison);
  const activeSystem = useApp((s) => s.activeSystem);
  const blind = useApp((s) => s.blind);
  const setBlind = useApp((s) => s.setBlind);
  const switchSystem = useApp((s) => s.switchSystem);
  const engineSettings = useApp((s) => s.project.engine);
  const setEngine = useApp((s) => s.setEngine);

  return (
    <div className="panel-section">
      <SectionTitle>Comparison</SectionTitle>
      {comparison !== 'single' && (
        <div className="grid2" style={{ marginBottom: 6 }}>
          {(['A', 'B'] as SystemSlotKey[]).map((k) => (
            <button
              key={k}
              className={`btn${activeSystem === k ? ' on' : ''}`}
              onClick={() => switchSystem(k)}
            >
              System {k}
            </button>
          ))}
        </div>
      )}
      <div className="field">
        <label>Transition</label>
        <Segmented
          small
          value={engineSettings.transitionMs}
          onChange={(v) => setEngine({ transitionMs: v })}
          options={[
            { value: 5, label: '5' },
            { value: 10, label: '10' },
            { value: 25, label: '25' },
            { value: 50, label: '50' },
            { value: 100, label: '100' },
          ]}
          ariaLabel="Transition length in milliseconds"
        />
        <span className="val">ms</span>
      </div>
      <Toggle
        label="Blind — hide model names"
        checked={blind}
        onChange={setBlind}
        title="Identities are hidden everywhere until you turn this off"
      />
    </div>
  );
}

function LevelMatchSection() {
  const lm = useApp((s) => s.project.levelMatch);
  const setLevelMatch = useApp((s) => s.setLevelMatch);
  const result = useApp((s) => s.levelMatchResult);
  const matching = useApp((s) => s.levelMatching);
  const runLevelMatch = useApp((s) => s.runLevelMatch);
  const hasFile = useApp((s) => !!s.buffer);

  return (
    <div className="panel-section">
      <SectionTitle>Level matching</SectionTitle>
      <div className="field">
        <label>Metric</label>
        <Segmented
          small
          value={lm.mode}
          onChange={(v) => setLevelMatch({ mode: v })}
          options={[
            { value: 'off', label: 'Off' },
            { value: 'peak', label: 'Peak' },
            { value: 'rms', label: 'RMS' },
            { value: 'lufs', label: 'LUFS' },
          ]}
          ariaLabel="Level match metric"
        />
      </div>
      <Slider label="Trim A" min={-12} max={12} step={0.05} value={lm.manualTrimA} onChange={(v) => setLevelMatch({ manualTrimA: v })} format={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)} dB`} />
      <Slider label="Trim B" min={-12} max={12} step={0.05} value={lm.manualTrimB} onChange={(v) => setLevelMatch({ manualTrimB: v })} format={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)} dB`} />
      <button className="btn wide sm" onClick={() => void runLevelMatch()} disabled={!hasFile || lm.mode === 'off' || matching}>
        {matching ? 'Measuring…' : 'Re-measure now'}
      </button>
      {result && result.mode !== 'off' && (
        <div style={{ marginTop: 7 }}>
          <div className="stat-grid">
            <Stat label="System A" value={`${result.trimA >= 0 ? '+' : ''}${result.trimA.toFixed(2)}`} sub="dB applied" />
            <Stat label="System B" value={`${result.trimB >= 0 ? '+' : ''}${result.trimB.toFixed(2)}`} sub="dB applied" />
          </div>
          <p className="faint tiny" style={{ marginBottom: 0 }}>
            Measured by rendering {result.a.seconds.toFixed(1)} s of this file through each complete chain.
          </p>
        </div>
      )}
      {lm.mode === 'off' && (
        <Note tone="warn">
          Level matching is off. Loudness differences between chains will dominate any comparison you make.
        </Note>
      )}
    </div>
  );
}

function BankSection() {
  const banks = useApp((s) => s.project.banks);
  const activeBank = useApp((s) => s.activeBank);
  const selectBank = useApp((s) => s.selectBank);
  const setBank = useApp((s) => s.setBank);

  return (
    <div className="panel-section">
      <SectionTitle>Rapid switch — DAC banks</SectionTitle>
      <p className="faint tiny" style={{ marginTop: 0 }}>
        Assign from the library, then press 1-4 to switch while the music plays.
      </p>
      {[0, 1, 2, 3].map((i) => {
        const id = banks.dac[i];
        const p = registry.get(id);
        return (
          <div className="row" key={i} style={{ marginBottom: 4 }}>
            <button
              className={`btn sm${activeBank.dac === i ? ' on' : ''}`}
              style={{ width: 26, flex: 'none' }}
              disabled={!p}
              onClick={() => selectBank('dac', i)}
            >
              {i + 1}
            </button>
            <span className="small" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p ? `${p.manufacturer} ${p.model}` : <span className="faint">empty</span>}
            </span>
            {p && (
              <button className="btn sm" onClick={() => setBank('dac', i, null)} title="Clear">
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SavedSystems() {
  const [systems, setSystems] = useState<storage.SavedSystem[]>([]);
  const [name, setName] = useState('');
  const project = useApp((s) => s.project);
  const activeSystem = useApp((s) => s.activeSystem);
  const log = useApp((s) => s.log);
  const recompile = useApp((s) => s.recompile);

  const refresh = () => void storage.listSystems().then(setSystems);
  useEffect(refresh, []);

  const save = async () => {
    const label = name.trim() || `System ${new Date().toLocaleString()}`;
    const snapshot = {
      id: `sys_${Date.now()}`,
      label,
      timestamp: Date.now(),
      systemA: project.systemA,
      systemB: project.systemB,
      engine: project.engine,
      contextSampleRate: 0,
      profileVersions: {} as Record<string, number>,
    };
    for (const id of [project.systemA.dacId, project.systemA.ampId, project.systemA.outputId]) {
      const p = registry.get(id);
      if (p) snapshot.profileVersions[p.id] = p.version;
    }
    await storage.saveSystem({ id: snapshot.id, name: label, created: new Date().toISOString(), snapshot });
    setName('');
    refresh();
    log('info', `Saved system "${label}".`);
  };

  return (
    <div className="panel-section">
      <SectionTitle>Saved systems</SectionTitle>
      <div className="row" style={{ marginBottom: 6 }}>
        <input
          type="text"
          placeholder="Name this chain..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void save()}
        />
        <button className="btn sm" onClick={() => void save()}>
          Save
        </button>
      </div>
      {systems.length === 0 && <p className="faint tiny" style={{ margin: 0 }}>Nothing saved yet. Everything stays on this machine.</p>}
      {systems.slice(-12).reverse().map((s) => (
        <div className="row" key={s.id} style={{ marginBottom: 3 }}>
          <button
            className="btn sm"
            style={{ flex: 1, justifyContent: 'flex-start', minWidth: 0 }}
            title={`Restore "${s.name}"`}
            onClick={() => {
              useApp.setState((st) => ({
                project: { ...st.project, systemA: s.snapshot.systemA, systemB: s.snapshot.systemB, engine: s.snapshot.engine },
              }));
              recompile(undefined, 0);
              log('info', `Restored system "${s.name}".`);
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
          </button>
          <button
            className="btn sm"
            onClick={() => void storage.deleteSystem(s.id).then(refresh)}
            title="Delete"
          >
            ×
          </button>
        </div>
      ))}
      <p className="faint tiny" style={{ marginBottom: 0, marginTop: 6 }}>
        Snapshots record hardware, DSP settings and profile versions, so a comparison stays reproducible even after a
        profile is updated ({activeSystem} is the live chain).
      </p>
    </div>
  );
}

function HistorySection() {
  const history = useApp((s) => s.history);
  const recent = history.slice(-14).reverse();
  return (
    <div className="panel-section">
      <SectionTitle>Recent hardware changes</SectionTitle>
      {recent.length === 0 && <p className="faint tiny" style={{ margin: 0 }}>No changes yet.</p>}
      <div className="console">
        {recent.map((h, i) => (
          <div className="line info" key={`${h.timestamp}-${i}`}>
            <span className="t">{fmtClock(h.timestamp)}</span>
            <span className="m">
              <b className="dim">{String(h.slot).toUpperCase()}</b> {h.fromLabel} → <b className="bright">{h.toLabel}</b>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function fmtClock(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

export { fmtTime };
